import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { HeaderSection } from "common/generator/components/property-sections";
import type { DeviceFormFactor } from "common/render/form-factor";
import { castDraft } from "immer";
import type { SelectMode } from "sharedUI/lib/editor-select-mode-utils";
import type { LeftPanelState, SelectedComponentPath } from "store/types";
import type { ThemePlatform } from "webapp/lib/theme/theme";
import { defaultRootState } from "./default-root-state";

const uiStateSlice = createSlice({
    name: "uistate",
    initialState: defaultRootState.uiState,
    reducers: {
        updateLeftPanelState: (state, action: PayloadAction<Partial<LeftPanelState>>) => {
            state.leftPanelState = {
                ...state.leftPanelState,
                ...action.payload,
            };
        },
        setPreviewTheme: (state, action: PayloadAction<ThemePlatform>) => {
            state.previewPlatform = action.payload;
        },
        setSelectedComponent: (state, action: PayloadAction<SelectedComponentPath | undefined>) => {
            state.selectedComponent = castDraft(action.payload);
            state.selectedColumn = undefined;
        },
        setSelectMode: (state, action: PayloadAction<SelectMode>) => {
            state.selectMode = action.payload;
        },
        setSelectedColumn: (state, action: PayloadAction<SelectedComponentPath | undefined>) => {
            state.selectedComponent = undefined;
            state.selectedColumn = castDraft(action.payload);
        },
        setMaxPageWidth: (state, action: PayloadAction<number | undefined>) => {
            state.maxPageWidth = action.payload;
        },
        setDeviceFormFactor: (state, action: PayloadAction<DeviceFormFactor>) => {
            state.deviceFormFactor = action.payload;
        },
        setDesiredHeaderSection: (state, action: PayloadAction<HeaderSection | undefined>) => {
            state.desiredHeaderSection = action.payload;
        },
        setHighlightedComponent: (state, action: PayloadAction<typeof defaultRootState.uiState.highlightComponent>) => {
            state.highlightComponent = action.payload;
        },
    },
    extraReducers: {
        "tables/addTemporaryColumnAction": state => {
            state.selectedComponent = undefined;
            state.selectedColumn = undefined;
        },
        "tables/setTemporaryColumnFromColumn": state => {
            state.selectedComponent = undefined;
            state.selectedColumn = undefined;
        },
        "tables/addTemporaryColumnFormatAction": state => {
            state.selectedComponent = undefined;
            state.selectedColumn = undefined;
        },
    },
});

export const {
    setPreviewTheme,
    setSelectMode,
    setSelectedComponent,
    updateLeftPanelState,
    setSelectedColumn,
    setDeviceFormFactor,
    setDesiredHeaderSection,
    setMaxPageWidth,
    setHighlightedComponent,
} = uiStateSlice.actions;
export default uiStateSlice.reducer;
